import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Trophy } from 'lucide-react';
import { AccessibilityIcons } from './Accessibility';

// Tiles must always show prose, never the address/tel/email blobs left over
// from legacy imports. Skip leading junk segments to find the real intro.
const JUNK_RE = /\btel\b[:\s]?|\bemail\b[:\s]?|https?:\/\/|www\.|\S+@\S+\.\S+|\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/i;

function extractProse(text) {
  if (!text) return '';
  const segments = text.split(/\n+|\s{2,}/).map((s) => s.trim()).filter(Boolean);
  const addressy = (seg) => {
    const words = seg.split(/\s+/);
    return words.length < 6 || JUNK_RE.test(seg) || (seg.split(',').length - 1) / words.length > 0.25;
  };
  return segments.filter((s) => !addressy(s)).join(' ');
}

export function cardBlurb(a) {
  const s = (a?.summary || '').trim();
  if (s && !JUNK_RE.test(s.slice(0, 120))) return s;
  return extractProse(a?.description) || extractProse(s) || '';
}
import SmartImage from './SmartImage';

// Headline tag for the card meta row. A venue with an on-site café carries a
// legitimate "Food & Drink" tag, but it must not masquerade as the venue type
// unless the record really is a food & drink outlet.
function headlineTag(a) {
  const tags = a.tags || [];
  if (!tags.length) return null;
  if (a.category === 'food_drink') return tags[0];
  return tags.find((t) => !/food|drink|caf[eé]/i.test(t)) || tags[0];
}

export default function AttractionCard({ a, size = 'md' }) {
  const tall = size === 'lg';
  const hasAward = a.awards_won && a.awards_won.length > 0;
  return (
    <Link
      to={`/explore/${a.slug}`}
      data-testid={`attraction-card-${a.slug}`}
      className="group bg-white rounded-3xl overflow-hidden border border-charcoal/10 block transition-all hover:shadow-soft hover:-translate-y-1"
    >
      <div className={`relative overflow-hidden ${tall ? 'aspect-[4/5]' : 'aspect-[4/3]'}`}>
        <SmartImage src={a.hero_image} alt={a.name} hoverZoom />
        {hasAward && (
          <span
            data-testid={`card-award-${a.slug}`}
            title={`${a.awards_won[0].category || 'Award'} ${a.awards_won[0].year} Winner`}
            className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-charcoal text-mustard text-xs font-semibold px-3 py-1.5 rounded-full ring-2 ring-mustard/30"
          >
            <Trophy size={11}/> Winner {a.awards_won[0].year}
          </span>
        )}
        {(a.stayplayexplore || a.perfectbreak || (a.nominee_badge && (!a.nominee_badge_until || new Date(a.nominee_badge_until + 'T23:59:59') >= new Date()))) && (
          <span className="absolute top-4 left-4 flex flex-col items-start gap-1.5 max-w-[calc(100%-2rem)]">
            {a.nominee_badge && (!a.nominee_badge_until || new Date(a.nominee_badge_until + 'T23:59:59') >= new Date()) && (
              <span data-testid={`card-nominee-badge-${a.slug}`} title={a.nominee_badge} className="inline-flex items-center bg-forest text-[#FAF8F5] text-[10px] leading-none font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap">
                {a.nominee_badge}
              </span>
            )}
            {a.stayplayexplore && (
              <span data-testid={`card-spe-badge-${a.slug}`} title="Part of our Stay Play Explore packages" className="inline-flex items-center bg-mustard text-charcoal text-[10px] leading-none font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap">
                Stay Play Explore packages
              </span>
            )}
            {a.perfectbreak && (
              <span data-testid={`card-pb-badge-${a.slug}`} title="Part of our Perfect Break packages" className="inline-flex items-center bg-heritage text-[#FAF8F5] text-[10px] leading-none font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap">
                Perfect Break packages
              </span>
            )}
          </span>
        )}
        {a.free_entry && (
          <span className="absolute top-4 right-4 bg-forest text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            Free
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2 text-muted text-sm">
          {a.town && (<><MapPin size={14} className="text-heritage" /><span>{a.town}</span></>)}
          {headlineTag(a) && <><span className="text-charcoal/30">·</span><span>{headlineTag(a)}</span></>}
          <div className="ml-auto"><AccessibilityIcons accessibility={a.accessibility} /></div>
        </div>
        <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-heritage transition-colors">{a.name}</h3>
        <p className="text-muted text-sm leading-relaxed line-clamp-2" data-testid={`card-blurb-${a.slug}`}>{cardBlurb(a).slice(0, 200)}</p>
        <div className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-heritage">
          View details
          <span className="transition-transform group-hover:translate-x-1" aria-hidden>→</span>
        </div>
      </div>
    </Link>
  );
}
